import _regeneratorRuntime from "/root/app/node_modules/@babel/runtime/regenerator";
import { put, take, select, cancelled } from 'redux-saga/effects';
import { REQUEST_SAGA } from './constants';
import { getError } from './utils';
/**
 * Creates a saga that automates the lifecycle of an API request. The request
 * flow/lifecycle can be found below.
 *
 * Default:
 * 1) Dispatch load action
 * 2) Wait for a success or failure action
 * 3) If successful, request is complete
 * 4) If failure, throw an Error
 *
 * Request already in-flight/loading:
 * 1) Wait for a success or failure action
 * 2) If successful, request is complete
 * 3) If failure, throw an Error
 *
 * Data is already loaded:
 * 1) If forceRefresh option is not true, do nothing
 * 2) If forceRefresh option is true, dispatch load action and resolve
 *
 * @global
 * @param {RequestActions} actions - Actions for a request type
 * @param {ReduxSelector} loadingSelector - Selector that returns loading state
 * @param {ReduxSelector} loadedSelector - Selector that returns loaded state
 * @param {Object} [options={}] - Additional options
 * @return {createRequestSaga~requestSaga} - The created request saga
 */

export function createRequestSaga(actions) {
  var _marked = /*#__PURE__*/_regeneratorRuntime.mark(requestSaga);

  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      loadingSelector = _ref.loadingSelector,
      loadedSelector = _ref.loadedSelector;

  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  /**
   * Saga that automates the lifecycle of an API request.
   * @param {...*} props - Props passed to the saga
   * @return {Generator} - The request saga generator
   * @see {@link createRequestSaga}
   * @inner
   */

  function requestSaga() {
    var _len,
        props,
        _key,
        dataLoaded,
        isLoading,
        action,
        error,
        _args = arguments;

    return _regeneratorRuntime.wrap(function requestSaga$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            for (_len = _args.length, props = new Array(_len), _key = 0; _key < _len; _key++) {
              props[_key] = _args[_key];
            }

            if (!loadedSelector) {
              _context.next = 10;
              break;
            }

            _context.next = 4;
            return select.apply(void 0, [loadedSelector].concat(props));

          case 4:
            dataLoaded = _context.sent;

            if (!dataLoaded) {
              _context.next = 10;
              break;
            }

            if (!options.forceRefresh) {
              _context.next = 9;
              break;
            }

            _context.next = 9;
            return put(actions.load.apply(actions, props));

          case 9:
            return _context.abrupt("return");

          case 10:
            if (!loadingSelector) {
              _context.next = 14;
              break;
            }

            _context.next = 13;
            return select.apply(void 0, [loadingSelector].concat(props));

          case 13:
            isLoading = _context.sent;

          case 14:
            if (isLoading) {
              _context.next = 17;
              break;
            }

            _context.next = 17;
            return put(actions.load.apply(actions, props));

          case 17:
            _context.prev = 17;
            _context.next = 20;
            return take([actions.success.getType(), actions.failure.getType()]);

          case 20:
            action = _context.sent;

          case 21:
            _context.prev = 21;
            _context.next = 24;
            return cancelled();

          case 24:
            if (!_context.sent) {
              _context.next = 27;
              break;
            }

            _context.next = 27;
            return put(actions.failure('Request cancelled!'));

          case 27:
            return _context.finish(21);

          case 28:
            if (!(action && action.type === actions.failure.getType())) {
              _context.next = 32;
              break;
            }

            error = getError(action);

            if (!error) {
              _context.next = 32;
              break;
            }

            throw error instanceof Error ? error : new Error(error);

          case 32:
          case "end":
            return _context.stop();
        }
      }
    }, _marked, null, [[17,, 21, 28]]);
  } // Attach input params to generator function below (primarily for testing purposes)

  /**
   * Raw actions provided to {@link createRequestSaga}.
   * @type {RequestActions}
   * @inner
   */


  requestSaga.actions = actions;
  /**
   * Loading selector provided to {@link createRequestSaga}.
   * @type {ReduxSelector}
   * @inner
   */

  requestSaga.loadingSelector = loadingSelector;
  /**
   * Loaded selector provided to {@link createRequestSaga}.
   * @type {ReduxSelector}
   * @inner
   */

  requestSaga.loadedSelector = loadedSelector;
  /**
   * Raw options provided to {@link createRequestSaga}.
   * @type {Object}
   * @inner
   */

  requestSaga.options = options;
  /**
   * Symbol indicating that {@link createRequestSaga~requestSaga} is a request saga.
   * @type {Boolean}
   * @inner
   */

  requestSaga[REQUEST_SAGA] = true;
  return requestSaga;
}
export default createRequestSaga;